import React, { useState } from "react";
import { validateLoggedIn, getSession } from "../utils";
import {
    TextField,
    MenuItem,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import NavBar from "../navbar";
import config from "../config";
import * as Sentry from "@sentry/react";

const CreatePlan = () => {
    const navigate = useNavigate();
    const [planData, setPlanData] = useState({
        name: "",
        usage_context: "standard",
        max_keys: 1,
        default_period: 30,
        default_count: 100,
        ten_second_limit: null,
        minutely_limit: null,
        hourly_limit: null,
        five_hour_limit: null,
        daily_limit: null,
        weekly_limit: null,
        monthly_limit: null,
        linkedin_five_hour_limit: null,
        linkedin_weekly_limit: null,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Check if user is logged in
    const validate_logged_in = validateLoggedIn();
    if (validate_logged_in) {
        return validate_logged_in;
    }

    const handleChange = (field, value) => {
        setPlanData({ ...planData, [field]: value });
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);

            // Validate inputs
            if (!planData.name.trim()) {
                setError("Plan name is required");
                setSaving(false);
                return;
            }

            if (planData.max_keys < 1) {
                setError("Max keys must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.default_period < 1) {
                setError("Default period must be at least 1 day");
                setSaving(false);
                return;
            }

            if (planData.default_count < 1) {
                setError("Default count must be at least 1");
                setSaving(false);
                return;
            }

            // Validate limit fields if they are provided (not null)
            if (planData.ten_second_limit !== null && planData.ten_second_limit < 1) {
                setError("Ten second limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.minutely_limit !== null && planData.minutely_limit < 1) {
                setError("Minutely limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.hourly_limit !== null && planData.hourly_limit < 1) {
                setError("Hourly limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.daily_limit !== null && planData.daily_limit < 1) {
                setError("Daily limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.monthly_limit !== null && planData.monthly_limit < 1) {
                setError("Monthly limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.five_hour_limit !== null && planData.five_hour_limit < 1) {
                setError("Five hour limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.weekly_limit !== null && planData.weekly_limit < 1) {
                setError("Weekly limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.linkedin_five_hour_limit !== null && planData.linkedin_five_hour_limit < 1) {
                setError("LinkedIn five hour limit must be at least 1");
                setSaving(false);
                return;
            }

            if (planData.linkedin_weekly_limit !== null && planData.linkedin_weekly_limit < 1) {
                setError("LinkedIn weekly limit must be at least 1");
                setSaving(false);
                return;
            }

            // Ensure numeric fields are converted to numbers
            const dataToSend = {
                name: planData.name,
                usage_context: planData.usage_context,
                max_keys: parseInt(planData.max_keys),
                default_period: parseInt(planData.default_period),
                default_count: parseInt(planData.default_count),
                ten_second_limit: planData.ten_second_limit !== null ? parseInt(planData.ten_second_limit) : null,
                minutely_limit: planData.minutely_limit !== null ? parseInt(planData.minutely_limit) : null,
                hourly_limit: planData.hourly_limit !== null ? parseInt(planData.hourly_limit) : null,
                five_hour_limit: planData.five_hour_limit !== null ? parseInt(planData.five_hour_limit) : null,
                daily_limit: planData.daily_limit !== null ? parseInt(planData.daily_limit) : null,
                weekly_limit: planData.weekly_limit !== null ? parseInt(planData.weekly_limit) : null,
                monthly_limit: planData.monthly_limit !== null ? parseInt(planData.monthly_limit) : null,
                linkedin_five_hour_limit: planData.linkedin_five_hour_limit !== null ? parseInt(planData.linkedin_five_hour_limit) : null,
                linkedin_weekly_limit: planData.linkedin_weekly_limit !== null ? parseInt(planData.linkedin_weekly_limit) : null
            };

            const response = await fetch(config.apiBaseUrl + "/admin/plans", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": getSession().token,
                },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || `Error: ${response.status} ${response.statusText}`);
            }

            setSuccess(true);
            setTimeout(() => {
                navigate("/plans");
            }, 1500);
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <NavBar />
            <Box sx={{ maxWidth: 800, margin: "auto", padding: 3 }}>
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h5" gutterBottom>
                        Create New Plan
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Plan created successfully!
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Plan Name"
                                value={planData.name}
                                onChange={(e) => handleChange("name", e.target.value)}
                                disabled={saving}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                margin="normal"
                                label="Usage Context"
                                value={planData.usage_context}
                                onChange={(e) => handleChange("usage_context", e.target.value)}
                                disabled={saving}
                                helperText="mcp_only = MCP plan (not usable via REST API)"
                            >
                                <MenuItem value="standard">standard</MenuItem>
                                <MenuItem value="mcp_only">mcp_only</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Max Keys"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.max_keys}
                                onChange={(e) => handleChange("max_keys", parseInt(e.target.value) || 1)}
                                disabled={saving}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Default Period (days)"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.default_period}
                                onChange={(e) => handleChange("default_period", parseInt(e.target.value) || 1)}
                                disabled={saving}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                required
                                margin="normal"
                                label="Default Count (points)"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.default_count}
                                onChange={(e) => handleChange("default_count", parseInt(e.target.value) || 1)}
                                disabled={saving}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Ten Second Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.ten_second_limit === null ? "" : planData.ten_second_limit}
                                onChange={(e) => handleChange("ten_second_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Minutely Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.minutely_limit === null ? "" : planData.minutely_limit}
                                onChange={(e) => handleChange("minutely_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Hourly Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.hourly_limit === null ? "" : planData.hourly_limit}
                                onChange={(e) => handleChange("hourly_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Daily Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.daily_limit === null ? "" : planData.daily_limit}
                                onChange={(e) => handleChange("daily_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Monthly Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.monthly_limit === null ? "" : planData.monthly_limit}
                                onChange={(e) => handleChange("monthly_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Leave empty for no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Session (5h) Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.five_hour_limit === null ? "" : planData.five_hour_limit}
                                onChange={(e) => handleChange("five_hour_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Points per 5h. Empty = no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Weekly Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.weekly_limit === null ? "" : planData.weekly_limit}
                                onChange={(e) => handleChange("weekly_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Points per week. Empty = no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="LinkedIn (5h) Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.linkedin_five_hour_limit === null ? "" : planData.linkedin_five_hour_limit}
                                onChange={(e) => handleChange("linkedin_five_hour_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Points per 5h on /linkedin. Empty = no limit"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="LinkedIn (weekly) Limit"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={planData.linkedin_weekly_limit === null ? "" : planData.linkedin_weekly_limit}
                                onChange={(e) => handleChange("linkedin_weekly_limit", e.target.value === "" ? null : parseInt(e.target.value) || null)}
                                disabled={saving}
                                helperText="Points per week on /linkedin. Empty = no limit"
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={saving && <CircularProgress size={16} />}
                        >
                            {saving ? "Saving..." : "Create Plan"}
                        </Button>

                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => navigate("/plans")}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </>
    );
};

export default CreatePlan;
